import { memo, useContext, useEffect } from "react";
import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { DataStore } from '@aws-amplify/datastore'

import { 
  BsXCircle,
  BsCheck2,
  BsCheck2All 
} from 'react-icons/bs'
import { TbHandStop } from 'react-icons/tb'

import { 
  DgtPdOrderOperation, 
  DgtPdOrderOperationLog, 
  DgtPdProductionOrder 
} from "../../../models";
import { OperationTimeQC } from "../../../constants/Enum";
import Modal from "../../../components/Modal";
import UserContext from "../../../contexts/UserContext";
import { BulkStatus } from "../../../models";
import { formatDateFromUnix, formatDateObject } from '../../../utilities/Format'
import { convertJson } from "../../../utilities/Validate";

export default memo(function BulkInspectForm(
  { show, onHide, productionOrder, operation, editLogId } : { 
    show: boolean, 
    onHide: () => void, 
    productionOrder : DgtPdProductionOrder,
    operation: DgtPdOrderOperation,
    editLogId?: string
  }) {

  const userCon = useContext(UserContext)
  const user : any = userCon.user

  const nowUnix = Math.round(Date.now() / 1000)
  
  const batchNo = productionOrder.actualBatchNumbers ? convertJson(productionOrder.actualBatchNumbers) as string[] : []

  const schema = yup.object({
    bulkStatus: yup.mixed<BulkStatus>().required('กรุณาเลือก BULK Status'),
    bulkStatusComment: yup.string().optional()
  })

  const {
    register, handleSubmit, formState: { errors }, reset, watch, setValue, control
  } = useForm<DgtPdOrderOperationLog>({
    defaultValues: {
      timeType: OperationTimeQC.INSPECT_BULK,
      productionOrderId: productionOrder.id,
      operationNo: operation.operationNo,
      batchNo: batchNo.length > 0 ? batchNo[0] : '',
      postingDate: formatDateObject(new Date()),
      workCenterId: operation.workCenterId,
      actualWorkCenterId: operation.actualWorkCenterId ? operation.actualWorkCenterId : undefined,
      mch: "",
      startedTime: nowUnix,
      finishedTime: nowUnix,
      totalTime: 0,
      downtimeCode: "180",
      downtimeDescription: "รอ Bulk Passed (30&45 min)",
      downtimeBaseTime: 0,
      pdOrderOperationId: operation.id,
      createdBy: user?.name || user?.username,
      createdVersion: 'c9ed51c',
      materialCode: productionOrder.materialCode,
      materialDescription: productionOrder.materialDescription
    },
    resolver: yupResolver(schema)
  })

  useEffect(() => {
    const fetchLog = async (logId?: string) => {
      if (!logId) return
  
      const originaLog = await DataStore.query(DgtPdOrderOperationLog, logId)
      if (originaLog) {
        setValue('id', originaLog.id)
        setValue('bulkStatus', originaLog.bulkStatus)
        setValue('bulkStatusComment', originaLog.bulkStatusComment)
        setValue('bulkStatusBy', originaLog.bulkStatusBy)
        setValue('bulkStatusDateTime', originaLog.bulkStatusDateTime)
        setValue('approveBulkStatusBy', originaLog.approveBulkStatusBy)
        setValue('approveBulkStatusDateTime', originaLog.approveBulkStatusDateTime)
      }
    }
    fetchLog(editLogId)
  }, [ editLogId, setValue ])

  const onSubmitForm: SubmitHandler<DgtPdOrderOperationLog> = async (data) => {
    const passTime = Math.floor(Date.now() / 1000)
    if (data.id) {
      const originaLog = await DataStore.query(DgtPdOrderOperationLog, data.id)
      if (originaLog) {
        const updateLog = DgtPdOrderOperationLog.copyOf(originaLog, updated => {
          updated.bulkStatus = data.bulkStatus
          updated.bulkStatusComment = data.bulkStatusComment
          updated.bulkStatusBy = user?.name || user?.username
          updated.bulkStatusDate = formatDateObject(new Date())
          updated.bulkStatusDateTime = passTime
          updated.startedTime = passTime
          updated.finishedTime = passTime
          updated.updatedVersion = 'c9ed51c'
          updated.updatedBy = user?.name || user?.username
        })
        const updated = await DataStore.save(updateLog)
        console.log('updated', updated)
      }
    } else {
      const payload = { ...data }
      payload.bulkStatusBy = user?.name || user?.username
      payload.bulkStatusDate = formatDateObject(new Date())
      payload.bulkStatusDateTime = passTime
      payload.startedTime = passTime
      payload.finishedTime = passTime
      const inserted = await DataStore.save(new DgtPdOrderOperationLog(payload))
      console.log('inserted', inserted)
    }
    completeActions()
  }

  const completeActions = () => {
    reset()
    onHide()
  }
  const bulkStatusBy = watch('bulkStatusBy')
  const bulkStatusDateTime = watch('bulkStatusDateTime')
  const approveBulkStatusBy = watch('approveBulkStatusBy')
  const approveBulkStatusDateTime = watch('approveBulkStatusDateTime')

  return <Modal
    show={show}
    onHide={() => {
      completeActions()
    }}
    title={'ลงบันทึกผลการทดสอบ BULK'}
    maxWidth='max-w-xl'
    >
    <form className="space-y-3 " onSubmit={handleSubmit(onSubmitForm)}>
      <p className="text-sm font-light text-center">สำหรับปรับผ้านุ่ม / แป้ง หรือ BULK อื่นๆที่ PD ไม่ได้เปิด DW 180/188 มา</p>
      <div className='grid grid-cols-2 divide-x'>
        <div className="pr-3 ">
          <h6 className="font-bold text-right">PD Order</h6>
          <h6 className="font-bold text-right">BULK Code</h6>
          <h6 className="font-bold text-right">Description</h6>
          <h6 className="font-bold text-right">Batch Lot</h6>
        </div>
        <div className="pl-3 ">
          <h6>{productionOrder.id}</h6>
          <h6>{productionOrder.materialCode}</h6>
          <h6>{productionOrder.materialDescription}</h6>
          <h6>{batchNo}</h6>
        </div>
      </div>
      <h6 className="underline ">ผลการทดสอบ</h6>
      <div className="flex flex-row gap-2">
        <Controller
          name="bulkStatus"
          control={control}
          render={({ field }) => <>
            {Object.keys(BulkStatus).map(blkSts => 
              <div className="relative bulk-status basis-1/3" key={blkSts}>
                <input 
                  className="hidden peer" type="radio" id={`radio${blkSts}`}
                  { ...field }
                  value={blkSts}
                  checked={field.value === blkSts}
                  name="bulkStatus"
                  disabled={approveBulkStatusDateTime! > 0}
                  onChange={(e) => field.onChange(e.currentTarget.value as BulkStatus)}
                />
                {blkSts === BulkStatus.PASS && 
                <label className="label-bulk-status peer-checked:border-green-600 peer-checked:text-green-600 peer-checked:bg-green-100 peer-disabled:text-gray-400" htmlFor={`radio${blkSts}`}>
                  {blkSts === BulkStatus.PASS && <BsCheck2 size={24} className='text-green-600' />}
                  <span>{blkSts}</span>
                </label>}
                {blkSts === BulkStatus.HOLD && 
                <label className="label-bulk-status peer-checked:border-yellow-600 peer-checked:text-yellow-600 peer-checked:bg-yellow-100 peer-disabled:text-gray-400" htmlFor={`radio${blkSts}`}>
                  {blkSts === BulkStatus.HOLD && <TbHandStop size={24} className='text-yellow-600' />}
                  <span>{blkSts}</span>
                </label>}
                {blkSts === BulkStatus.REJECT && 
                <label className="label-bulk-status peer-checked:border-red-600 peer-checked:text-red-600 peer-checked:bg-red-100 peer-disabled:text-gray-400" htmlFor={`radio${blkSts}`}>
                  {blkSts === BulkStatus.REJECT && <BsXCircle size={24} className='text-red-600' />}
                  <span>{blkSts}</span>
                </label>}
              </div>
            )}
          </>}
        />
      </div>
      <div>
        <small>Comment</small>
        <textarea
          className={`input-form-text`} placeholder="ระบุเหตุผล (ถ้ามี)"
          disabled={approveBulkStatusDateTime! > 0}
          { ...register('bulkStatusComment') }
        />
      </div>
      <div className="grid grid-cols-2">
        <div className="text-center ">
          <small className="flex justify-center gap-1 text-muted"><BsCheck2 size={20} className='text-green-600' />บันทึกโดย</small>
          <h6>{bulkStatusBy}</h6>
          <h6>{formatDateFromUnix(bulkStatusDateTime)}</h6>
        </div>
        <div className="text-center ">
          <small className="flex justify-center gap-1 text-muted"><BsCheck2All size={20} className='text-green-600' />อนุมัติโดย</small>
          <h6>{approveBulkStatusBy}</h6>
          <h6>{formatDateFromUnix(approveBulkStatusDateTime)}</h6>
        </div>
      </div>
      {errors.bulkStatus ? <label className="float-right p-3 italic text-center text-red-500">กรุณาเลือกผลการทดสอบ</label> : null}
      <div className="flex w-full gap-2 mt-4">
        <button type='button' className="text-base basis-1/2 btn-outline-primary" onClick={() => {
          completeActions()
        }}>
          ยกเลิก
        </button>
        <button
          type='submit'
          className="basis-1/2 btn-primary"
          disabled={approveBulkStatusDateTime! > 0}
        >บันทึก</button>
      </div>
    </form>
  </Modal>
})